import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { getPublicActivity, getPublicStats } from "@/lib/api";

function relTime(iso) {
  if (!iso) return "just now";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function LiveActivityStrip() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [idx, setIdx] = useState(0);

  const load = () => {
    getPublicActivity().then((d) => setItems(d.items || [])).catch(() => {});
    getPublicStats().then(setStats).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length && !stats) return null;

  // Item 3 (per user directive): hide the live ticker until we're seeing real
  // daily traction — >= 5 waitlist joins in the last 24 hours. Otherwise a
  // "1 joined in the last 24 hours" line reads as a negative signal.
  const joinsLast24h = stats?.joins_last_24h ?? 0;
  const joinsLastHour = stats?.joins_last_hour ?? 0;
  if (joinsLast24h < 5 && joinsLastHour < 1) return null;

  const current = items[idx];

  return (
    <section className="py-8 border-y border-white/5" data-testid="live-activity-strip">
      <div className="container-x">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-sm">
          {/* Rotating recent join */}
          {current ? (
            <div className="flex items-center gap-2.5 min-w-0 max-w-[92vw]" data-testid="live-activity-item">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon-cyan opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-neon-cyan" />
              </span>
              <AnimatePresence mode="wait">
                <motion.p
                  key={`${current.name}-${current.created_at}-${idx}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.35 }}
                  className="text-zinc-300 truncate"
                >
                  <span className="text-white font-medium">{current.name}</span>
                  <span className="text-zinc-500"> joined GateMate · </span>
                  <span className="text-neon-cyan">{relTime(current.created_at)}</span>
                </motion.p>
              </AnimatePresence>
            </div>
          ) : null}

          {/* Hourly momentum badge */}
          {stats && (joinsLastHour > 0 || joinsLast24h > 0) ? (
            <div className="hidden md:flex items-center gap-2 text-zinc-400" data-testid="live-activity-momentum">
              <Sparkles className="w-3.5 h-3.5 text-neon-cyan" />
              <span>
                <span className="text-white font-semibold tabular-nums">
                  {joinsLastHour > 0 ? joinsLastHour : joinsLast24h}
                </span>{" "}
                {joinsLastHour > 0 ? "joined in the last hour" : "joined in the last 24 hours"}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
